// src/pages/Ledger.jsx
import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useApp } from '../context/AppContext'
import { EmptyState } from '../components/UI'
import { fmt, fmtDate } from '../utils/helpers'

export default function Ledger() {
  const { t } = useTranslation()
  const { accounts, transactions, acctName } = useApp()
  const [selAcct, setSelAcct] = useState('')

  const ledgerData = useMemo(() => {
    const toShow = selAcct ? accounts.filter(a => a.id === selAcct) : accounts
    return toShow.map(acct => {
      const lines = []
      ;[...transactions]
        .sort((a, b) => a.date.localeCompare(b.date) || a.ref.localeCompare(b.ref))
        .forEach(txn => {
          if (txn.type === 'income'   && txn.acct === acct.id) lines.push({ date: txn.date, ref: txn.ref, desc: t('ledger.income_entry', { cat: txn.cat }) + (txn.note ? ' | ' + txn.note : ''), in: txn.amt, out: 0 })
          if (txn.type === 'expense'  && txn.acct === acct.id) lines.push({ date: txn.date, ref: txn.ref, desc: t('ledger.expense_entry', { cat: txn.cat }) + (txn.note ? ' | ' + txn.note : ''), in: 0, out: txn.amt })
          if (txn.type === 'transfer' && txn.from === acct.id) lines.push({ date: txn.date, ref: txn.ref, desc: t('ledger.transfer_out', { name: acctName(txn.to) }), in: 0, out: txn.amt })
          if (txn.type === 'transfer' && txn.to   === acct.id) lines.push({ date: txn.date, ref: txn.ref, desc: t('ledger.transfer_in', { name: acctName(txn.from) }), in: txn.amt, out: 0 })
        })
      const adj = lines.reduce((s, l) => s + l.in - l.out, 0)
      let run = acct.balance - adj
      const rows = lines.map(l => { run += l.in - l.out; return { ...l, balance: run } })
      return { acct, rows }
    })
  }, [accounts, transactions, selAcct, acctName, t])

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <select className="select" style={{ maxWidth: 320 }} value={selAcct} onChange={e => setSelAcct(e.target.value)}>
          <option value="">{t('ledger.all_accounts')}</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      {ledgerData.length === 0 && <EmptyState icon="ti-book-2" message={t('ledger.no_accounts')} sub={t('ledger.no_accounts_sub')} />}

      {ledgerData.map(({ acct, rows }) => {
        const bal      = acct.balance
        const totalIn  = rows.reduce((s, r) => s + r.in,  0)
        const totalOut = rows.reduce((s, r) => s + r.out, 0)

        return (
          <div key={acct.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--surface-alt)', borderBottom: '1px solid var(--border)' }}>
              <span style={{ width: 13, height: 13, borderRadius: '50%', background: acct.color, display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontWeight: 700, flex: 1, fontSize: 15 }}>{acct.name}</span>
              <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 20, background: 'var(--brand-50)', color: 'var(--brand-800)', fontWeight: 600 }}>
                {t(`account_types.${acct.type}`, acct.type)}
              </span>
              <span style={{ fontSize: 12, color: 'var(--green-600)', fontWeight: 600 }}>{t('ledger.in')}: ₹{fmt(totalIn)}</span>
              <span style={{ fontSize: 12, color: 'var(--red-600)',   fontWeight: 600 }}>{t('ledger.out')}: ₹{fmt(totalOut)}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: bal >= 0 ? 'var(--brand-600)' : 'var(--red-600)', marginLeft: 4 }}>
                ₹{fmt(Math.abs(bal))} {bal >= 0 ? t('common.dr') : t('common.cr')}
              </span>
            </div>

            {rows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '18px 20px', color: 'var(--gray-400)', fontSize: 13 }}>{t('ledger.no_transactions')}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>{t('ledger.date')}</th>
                      <th style={{ width: 72 }}>{t('ledger.ref')}</th>
                      <th>{t('ledger.particulars')}</th>
                      <th style={{ width: 100, textAlign: 'right' }}>{t('ledger.in')}</th>
                      <th style={{ width: 100, textAlign: 'right' }}>{t('ledger.out')}</th>
                      <th style={{ width: 110, textAlign: 'right' }}>{t('ledger.balance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td className="mono">{fmtDate(r.date)}</td>
                        <td className="mono">{r.ref}</td>
                        <td style={{ whiteSpace: 'normal', lineHeight: 1.4, fontSize: 12 }}>{r.desc}</td>
                        <td style={{ textAlign: 'right' }} className="c-income">{r.in  > 0 ? '₹' + fmt(r.in)  : ''}</td>
                        <td style={{ textAlign: 'right' }} className="c-expense">{r.out > 0 ? '₹' + fmt(r.out) : ''}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: r.balance >= 0 ? 'var(--brand-600)' : 'var(--red-600)' }}>
                          ₹{fmt(Math.abs(r.balance))} {r.balance >= 0 ? t('common.dr') : t('common.cr')}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--surface-alt)', fontWeight: 700 }}>
                      <td colSpan={5} style={{ textAlign: 'right', paddingRight: 14, color: 'var(--gray-600)', fontSize: 12 }}>{t('ledger.closing_balance')}</td>
                      <td style={{ textAlign: 'right', color: bal >= 0 ? 'var(--brand-600)' : 'var(--red-600)' }}>
                        ₹{fmt(Math.abs(bal))} {bal >= 0 ? t('common.dr') : t('common.cr')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}
